import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PrivacyNote } from "@/components/PrivacyNote";
import { Target, Plus, Trash2, Minus, PlusCircle, HeartPulse, Sparkles, Moon, Users, Brain, Scale, Leaf } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const CATS = ["reduce anxiety", "improve confidence", "sleep better", "be more social", "reduce overthinking", "work-life balance", "wellbeing"];

const CAT_STYLE = {
  anxiety: { icon: HeartPulse, bar: "#f43f5e", bg: "bg-rose-500/10", text: "text-rose-500" },
  confidence: { icon: Sparkles, bar: "#f59e0b", bg: "bg-amber-500/10", text: "text-amber-500" },
  sleep: { icon: Moon, bar: "#6366f1", bg: "bg-indigo-500/10", text: "text-indigo-500" },
  social: { icon: Users, bar: "#3b82f6", bg: "bg-blue-500/10", text: "text-blue-500" },
  overthink: { icon: Brain, bar: "#8b5cf6", bg: "bg-violet-500/10", text: "text-violet-500" },
  balance: { icon: Scale, bar: "#14b8a6", bg: "bg-teal-500/10", text: "text-teal-500" },
  wellbeing: { icon: Leaf, bar: "#10b981", bg: "bg-emerald-500/10", text: "text-emerald-500" },
};
const catStyle = (c) => {
  const k = (c || "").toLowerCase();
  if (k.includes("anx")) return CAT_STYLE.anxiety;
  if (k.includes("confiden")) return CAT_STYLE.confidence;
  if (k.includes("sleep")) return CAT_STYLE.sleep;
  if (k.includes("social")) return CAT_STYLE.social;
  if (k.includes("overthink")) return CAT_STYLE.overthink;
  if (k.includes("balance") || k.includes("work")) return CAT_STYLE.balance;
  return CAT_STYLE.wellbeing;
};

export default function Goals() {
  const { t } = useI18n();
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "wellbeing" });

  const load = () => api.get("/goals").then(({ data }) => setGoals(data.goals || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Give your goal a name");
    try { await api.post("/goals", form); setOpen(false); setForm({ title: "", description: "", category: "wellbeing" }); load(); toast.success("Goal set"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setProgress = async (id, p) => { await api.put(`/goals/${id}/progress`, { progress: Math.max(0, Math.min(100, p)) }); load(); };
  const del = async (id) => { await api.delete(`/goals/${id}`); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("goals.title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("goals.subtitle")}</p>
          <PrivacyNote className="mt-3" />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="new-goal-button"><Plus className="h-4 w-4" strokeWidth={1.5} /> {t("goals.newGoal")}</Button></DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{t("goals.dialogTitle")}</DialogTitle><DialogDescription>{t("goals.dialogDesc")}</DialogDescription></DialogHeader>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("goals.titlePh")} data-testid="goal-title-input" className="rounded-xl" />
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("goals.descPh")} data-testid="goal-desc-input" className="rounded-2xl resize-none" />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-xl" data-testid="goal-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={create} className="rounded-full" data-testid="save-goal-button">{t("goals.setGoal")}</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        {goals.length === 0 && <p className="text-sm text-muted-foreground">{t("goals.empty")}</p>}
        {goals.map((g, i) => {
          const st = catStyle(g.category);
          return (
          <motion.div key={g.goal_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.05 }}
                      className="ambient-shadow group rounded-3xl border border-border/60 bg-card p-6" data-testid={`goal-card-${g.goal_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl ${st.bg} ${st.text}`}>
                  <st.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <span className="font-serif text-lg">{g.title}</span>
                  {g.category && <span className={`mt-0.5 block text-xs font-medium capitalize ${st.text}`}>{g.category}</span>}
                </div>
              </div>
              <button onClick={() => del(g.goal_id)} className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" data-testid={`delete-goal-${g.goal_id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
            </div>
            {g.description && <p className="mt-3 text-sm text-muted-foreground">{g.description}</p>}
            <div className="mt-4 flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${g.progress}%`, backgroundColor: st.bar }} data-testid={`goal-bar-${g.goal_id}`} />
              </div>
              <span className="font-serif text-sm" style={{ color: st.bar }}>{g.progress}%</span>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => setProgress(g.goal_id, g.progress - 10)} data-testid={`goal-minus-${g.goal_id}`}><Minus className="h-3 w-3" /></Button>
              <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => setProgress(g.goal_id, g.progress + 10)} data-testid={`goal-plus-${g.goal_id}`}><PlusCircle className="h-3 w-3" /> {t("goals.progress")}</Button>
              {g.status === "completed" && <span className="ml-auto text-xs font-medium text-sage">{t("goals.completed")}</span>}
            </div>
          </motion.div>
          );
        })}
      </div>
    </div>
  );
}
